/**
 * *! This helper has unit tests.
 * If you make any changes to this file, you should run the unit tests:
 * npm test src/utest/calculate_PVSetup.test.ts
 */

import { convertToUnit } from 'src/helpers/units_helpers';
//Types
import type { EnergyDistributionPropsTypes } from '@Shelly/shelly-design-system/index';
import type { PVSetupTypes } from './pv_setup_helper';

export type PVLiveData = {
	gridPower: number;
	solarPower: number;
	houseConsumption: number;
	batteryPower: number;
	solarToGridPower: number;
	batteryLevel: number;
	pvSelfPower: number;
	solarToGrid: boolean;
	gridToSolar: boolean;
	solarToBattery: boolean;
	solarToHome: boolean;
	gridToBattery: boolean;
	gridToHome: boolean;
	batteryToHome: boolean;
	batteryToGrid: boolean;
	batteryStatus: string;
	batteryStatusIcon: string;
	homeProgress?: {
		id: string;
		percent: number;
		color: 'grid' | 'solar' | 'battery';
	}[];
	cards?: {
		icon: string;
		title: string;
		descriptiveText: string;
		value: string;
	}[];
	paths?: { id: string; percent: number; color: string }[];
};

/**
 * Returns a data object with the following properties:
 *
 * - gridPower: number
 * - solarPower: number
 * - houseConsumption: number
 * - batteryPower: number
 * - solarToGrid: boolean
 * - gridToSolar: boolean
 * - solarToBattery: boolean
 * - solarToHome: boolean
 * - gridToBattery: boolean
 * - gridToHome: boolean
 * - batteryToHome: boolean
 * - batteryToGrid: boolean
 * - homeProgress: { id: string, percent: number, color: string }[]
 * - cards: { icon: string, title: string, descriptiveText: string, value: string }[]
 * - paths: { id: string, percent: number, color: string }[]
 *
 * This function takes the following parameters:
 *
 * - gridPower: number - the power being drawn from the grid
 * - generatedPower: number - the power being generated by the solar panels
 * - pvSetupType: string - the type of PV setup (Parallel, BalconyPV, or BackUP)
 *
 * The function first calculates the house consumption based on the PV setup type.
 * Then it calculates the power being used from the grid, solar panels, and battery
 * based on the house consumption. It also sets flags for whether power is being
 * used from each source. Finally, it calculates the progress bars for each
 * source and returns the data object.
 */

export const getPvSetupStatuses = (
	gridPower: number,
	generatedPower: number,
	pvSetupType: 'BackUP' | 'Parallel' | 'BalconyPV',
	showBattery = false,
) => {
	let data: PVLiveData = getPVInitData();

	if (gridPower == 0 && generatedPower == 0) {
	} else if (pvSetupType === 'BackUP') {
		data = calculateBackUpFlow(gridPower, generatedPower, showBattery, data);
	} else if (pvSetupType === 'Parallel') {
		data = calculateParallelFlow(gridPower, generatedPower, showBattery, data);
	} else if (pvSetupType === 'BalconyPV') {
		data = calculateBalconyPVFlow(gridPower, generatedPower, showBattery, data);
	}
	data.homeProgress = [
		{
			id: '1',
			percent: toPercent(data.houseConsumption, data.gridPower),
			color: 'grid',
		},
		{
			id: '2',
			percent: toPercent(data.houseConsumption, data.solarPower),
			color: 'solar',
		},
		{
			id: '3',
			percent: toPercent(data.houseConsumption, data.batteryPower),
			color: 'battery',
		},
	];
	return {
		...data,
		cards: getCards(data, pvSetupType, showBattery),
		paths: getPaths(data),
		secondCards: getSecondCards(data, pvSetupType, showBattery),
		secondPaths: getSecondPaths(data),
	};
};

const calculateBackUpFlow = (
	gridPower: number,
	generatedPower: number,
	showBattery: boolean,
	data: PVLiveData,
) => {
	const grid = gridPower;
	const house = Math.abs(generatedPower);
	const solar = Math.max(0, house - grid);
	return calculatePVFlow(grid, solar, house, showBattery, data);
};

const calculateParallelFlow = (
	gridPower: number,
	generatedPower: number,
	showBattery: boolean,
	data: PVLiveData,
) => {
	const grid = gridPower;
	const solar = generatedPower;
	const house = Math.max(0, solar + grid);
	return calculatePVFlow(grid, solar, house, showBattery, data);
};

const calculateBalconyPVFlow = (
	gridPower: number,
	generatedPower: number,
	showBattery: boolean,
	data: PVLiveData,
) => {
	const grid = gridPower;
	const solar = Math.abs(generatedPower);
	const house = Math.max(0, solar + grid);
	return calculatePVFlow(grid, solar, house, showBattery, data);
};

const calculatePVFlow = (
	grid: number,
	solar: number,
	house: number,
	showBattery: boolean,
	data: PVLiveData,
) => {
	const totalSupply = solar + Math.max(0, grid);
	const excess = totalSupply - house;

	data.gridPower = grid;
	data.solarPower = solar;
	data.houseConsumption = house;

	data.gridToHome = grid > 0 && house > 0;
	data.solarToHome = solar > 0 && house > 0;

	if (excess > 0) {
		if (grid > 0) {
			data.batteryPower = excess;
			if (showBattery) {
				data.gridToBattery = true;
			} else if (solar > 0) {
				data.gridToSolar = true;
			}
		} else {
			data.batteryToGrid = solar + grid < 0;
			data.batteryPower = data.batteryToGrid ? Math.abs(solar + grid) : 0;
			data.solarToGrid = solar > 0;
		}
	} else if (grid < 0 && solar == 0 && house == 0) {
		data.batteryToGrid = true;
		data.batteryPower = Math.abs(grid);
	} else if (Math.abs(solar) > grid && house == 0) {
		data.solarToGrid = true;
	}

	const isNight = false;
	if (showBattery && data.solarToHome && isNight) {
		data.solarPower = 0;
		data.batteryPower = solar;
		data.batteryToHome = true;
		data.solarToHome = false;
	}
	return data;
};

export const getPVInitData = (): PVLiveData => {
	return {
		gridPower: 0,
		solarPower: 0,
		houseConsumption: 0,
		batteryPower: 0,
		pvSelfPower: 0,
		batteryLevel: 0,
		solarToGridPower: 0,
		solarToGrid: false,
		gridToSolar: false,
		solarToBattery: false,
		solarToHome: false,
		gridToBattery: false,
		gridToHome: false,
		batteryToHome: false,
		batteryToGrid: false,
		batteryStatus: null,
		batteryStatusIcon: null,
		homeProgress: [
			{ id: '1', percent: 0, color: 'grid' },
			{ id: '2', percent: 0, color: 'solar' },
			{ id: '3', percent: 0, color: 'battery' },
		],
	};
};

const getCards = (data: PVLiveData, pvSetupType: PVSetupTypes, showBattery: boolean) => {
	const unit = 'W';
	const cards = [
		{
			label: 'Home',
			icon: 'fas fa-home',
			progress: data.homeProgress,
			value1: { text: convertToUnit(data.houseConsumption, unit) },
			tooltip: [],
		},
		{
			label: 'Grid',
			icon: 'fas fa-utility-pole text-grid',
			progress: [{ id: '1', percent: 100, color: data.solarToGrid ? 'solar' : 'grid' }],
			value1: { text: convertToUnit(data.gridPower, unit) },
			tooltip: [],
		},
		{
			label: pvSetupType == 'BalconyPV' ? 'PV' : 'Solar',
			icon: 'fas fa-solar-panel text-solar',
			progress: [{ id: '2', percent: 100, color: 'solar' }],
			value1: { text: convertToUnit(Math.abs(data.solarPower), unit) },
			tooltip: [],
		},
	] as EnergyDistributionPropsTypes['cards'];
	if (showBattery) {
		cards.push({
			label: 'Invertor & batt',
			icon: 'fas fa-car-battery text-battery',
			progress: [{ id: '3', percent: 100, color: 'battery' }],
			value1: { text: convertToUnit(data.batteryPower, unit) },
			tooltip: [],
		});
	}
	return cards;
};

const getPaths = (data) => {
	return {
		leftToRight: {
			isActive: data.solarToGrid || data.gridToSolar,
			isReverse: data.gridToSolar,
			animationSeconds: 3,
		},
		leftToCenter: {
			isActive: data.gridToHome,
			animationSeconds: 3,
		},
		rightToCenter: {
			isActive: data.solarToHome,
			animationSeconds: 3,
		},
		leftToTopRight: {
			isActive: data.gridToBattery || data.batteryToGrid,
			isReverse: data.batteryToGrid,
			animationSeconds: 3,
		},
		topRightToCenter: {
			isActive: data.batteryToHome,
			animationSeconds: 3,
		},
		botToTop: {
			isActive: data.solarToBattery,
			animationSeconds: 3,
		},
	};
};

const getSecondCards = (data, pvSetupType: PVSetupTypes, showBattery: boolean) => {
	const unit = 'W';
	const cards = [
		{
			label: 'Home',
			icon: 'fas fa-home',
			progress: [{ id: '1', percent: 100, color: 'white' }],
			value1: { text: convertToUnit(data.houseConsumption, unit) },
			tooltip: [],
		},
		{
			label: pvSetupType == 'BalconyPV' ? 'PV Hub' : 'Invertor & batt',
			icon: 'fas fa-transformer-bolt',
			progress: [{ id: '2', percent: 100, color: 'white' }],
			value1: {
				text: convertToUnit(
					Math.abs(data.solarPower) + (showBattery ? 0 : -data.batteryPower),
					unit,
				),
				icon: showBattery ? 'fas fa-solar-panel text-solar' : '',
			},
			value2: {
				text: showBattery ? convertToUnit(data.batteryPower, unit) : '',
				icon: showBattery ? 'fas fa-car-battery text-battery' : '',
			},
			tooltip: [],
		},
		{
			label: 'Solar',
			icon: 'fas fa-solar-panel text-solar',
			progress: [{ id: '1', percent: 100, color: 'solar' }],
			value1: { text: convertToUnit(Math.abs(data.solarPower), unit) },
			tooltip: [],
		},
		{
			label: 'Grid',
			icon: 'fas fa-utility-pole text-grid',
			progress: [{ id: '1', percent: 100, color: 'grid' }],
			value1: { text: convertToUnit(data.gridPower, unit) },
			tooltip: [],
		},
	] as EnergyDistributionPropsTypes['cards'];
	return cards;
};

const getSecondPaths = (data) => {
	return {
		first: {
			isActive: data.solarToHome || data.gridToHome || data.gridToSolar,
			animationSeconds: 5,
		},
		second: { isActive: Math.abs(data.solarPower) > 0, animationSeconds: 2.5 },
		third: {
			isActive: data.gridToHome || data.gridToBattery || data.solarToGrid || data.batteryToGrid,
			isReverse: data.solarToGrid || data.batteryToGrid,
			animationSeconds: 5,
		},
	};
};

export const toPercent = (quantity: number, partOfAmount: number): number => {
	return Math.round((partOfAmount / quantity) * 100);
};
